import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 };
const INPUT = { width: "100%", padding: "7px 10px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)", background: "var(--surface, #fff)", boxSizing: "border-box" };

export function RecipientTypeToggle({ value, onChange }) {
  const opts = [{ id: "lead", label: "Leads" }, { id: "ehi_client", label: "EHI Clients" }];
  return (
    <div data-testid="qb-recipient-type-toggle" style={{ display: "inline-flex", padding: 2, borderRadius: 8, background: "var(--surface-2, #f1f5f9)", border: "1px solid var(--border)" }}>
      {opts.map((o) => (
        <button key={o.id} type="button" data-testid={`qb-recipient-type-${o.id}`} onClick={() => value !== o.id && onChange(o.id)}
          style={{ padding: "4px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "var(--font)",
            background: value === o.id ? "#fff" : "transparent", color: value === o.id ? "var(--qb-navy, #1e3a5f)" : "var(--text-tertiary)",
            boxShadow: value === o.id ? "0 1px 2px rgba(0,0,0,0.08)" : "none", transition: "background-color 120ms, color 120ms" }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function useEhiClientSearch(token, query) {
  const [state, setState] = useState({ items: [], loading: false, searched: false });
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setState({ items: [], loading: false, searched: false }); return undefined; }
    setState((s) => ({ ...s, loading: true }));
    let alive = true;
    const t = setTimeout(() => {
      axios.get(`${API}/ehi-clients/quote-search`, { headers: { Authorization: `Bearer ${token}` }, params: { q } })
        .then((r) => alive && setState({ items: r.data?.items || [], loading: false, searched: true }))
        .catch(() => alive && setState({ items: [], loading: false, searched: true }));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [token, query]);
  return state;
}

function ResultRow({ c, onPick }) {
  return (
    <button type="button" data-testid={`qb-ehi-result-${c.client_id}`} onMouseDown={(e) => { e.preventDefault(); onPick(c); }}
      className="qb-ehi-row" style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 10px", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontFamily: "var(--font)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      <strong>{c.clinic || "Unnamed clinic"}</strong>
      <span style={{ color: "var(--text-tertiary)" }}> · ID {c.client_id}{c.state_code ? ` · ${c.state_code}` : ""}{c.email ? ` · ${c.email}` : ""}</span>
    </button>
  );
}

export function EhiClientPicker({ token, value, onPick, onClear }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { items, loading, searched } = useEhiClientSearch(token, query);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (value?.client_id) {
    return (
      <div data-testid="qb-ehi-selected" style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12 }}>
        <strong style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value.clinic || "EHI client"}</strong>
        <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>ID {value.client_id}</span>
        <button type="button" data-testid="qb-ehi-clear" onClick={onClear} title="Clear" style={{ border: "none", background: "none", cursor: "pointer", padding: 0, color: "var(--text-tertiary)", display: "inline-flex" }}><X style={{ width: 13, height: 13 }} /></button>
      </div>
    );
  }
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <style>{`.qb-ehi-row:hover{background:#f5f7fb}`}</style>
      <Search style={{ position: "absolute", left: 9, top: 9, width: 13, height: 13, color: "var(--text-tertiary)" }} />
      <input data-testid="qb-ehi-search" value={query} placeholder="Search clinic, client ID, email, phone or state…"
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} style={{ ...INPUT, paddingLeft: 28 }} />
      {open && query.trim().length >= 2 && (
        <div data-testid="qb-ehi-results" style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 30, background: "#fff", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", maxHeight: 280, overflowY: "auto" }}>
          {loading && <div data-testid="qb-ehi-loading" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", fontSize: 11, color: "var(--text-tertiary)" }}><Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> Searching…</div>}
          {!loading && items.map((c) => <ResultRow key={c.client_id} c={c} onPick={(x) => { onPick(x); setQuery(""); setOpen(false); }} />)}
          {!loading && searched && items.length === 0 && <div data-testid="qb-ehi-empty" style={{ padding: "8px 10px", fontSize: 11, color: "var(--text-tertiary)" }}>No EHI clients match</div>}
        </div>
      )}
    </div>
  );
}

export function EhiClientFields({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const fields = [["clinic", "Clinic name"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"]];
  return (
    <div data-testid="qb-ehi-fields" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
      {fields.map(([k, label]) => (
        <div key={k}>
          <label style={LABEL}>{label}</label>
          <input data-testid={`qb-ehi-field-${k}`} value={value?.[k] || ""} onChange={set(k)} style={INPUT} />
        </div>
      ))}
      <div>
        <label style={LABEL}>EHI client ID</label>
        <input data-testid="qb-ehi-field-client-id" value={value?.client_id || ""} readOnly style={{ ...INPUT, background: "var(--surface-2, #f8fafc)", color: "var(--text-tertiary)" }} />
      </div>
    </div>
  );
}
